import React, { useState } from 'react';
import { contactAPI, newsletterAPI } from '../utils/api';
import { FadeIn, ScaleIn } from '../components/ScrollAnimation';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [newsletterError, setNewsletterError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await contactAPI.submit(formData);
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setFormData({ name: '', email: '', subject: '', message: '' });
      }, 4000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send message. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNewsletterError('');
    setNewsletterSuccess(false);

    try {
      await newsletterAPI.subscribe(newsletterEmail);
      setNewsletterSuccess(true);
      setNewsletterEmail('');
      setTimeout(() => setNewsletterSuccess(false), 4000);
    } catch (err) {
      setNewsletterError(err.response?.data?.error || 'Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <h1 className="page-title">Contact Us</h1>
      <div className="page-content">
        <FadeIn>
          <div className="section">
            <h2>Get In Touch</h2>
            <p>
              Have questions about our research, want to collaborate, or interested in
              joining our team? We'd love to hear from you!
            </p>
          </div>
        </FadeIn>

        <div className="card-grid">
          <ScaleIn delay={0.1}>
            <div className="card">
              <h3>📍 Location</h3>
              <p>
                AI Center of Excellence<br />
                123 Innovation Drive<br />
                Tech Valley, CA 94000<br />
                United States
              </p>
            </div>
          </ScaleIn>
          <ScaleIn delay={0.2}>
            <div className="card">
              <h3>📧 Email</h3>
              <p>
                General: info@aicoe.org<br />
                Research: research@aicoe.org<br />
                Careers: careers@aicoe.org<br />
                Press: press@aicoe.org
              </p>
            </div>
          </ScaleIn>
          <ScaleIn delay={0.3}>
            <div className="card">
              <h3>📞 Phone</h3>
              <p>
                Main: +1 (555) 123-4567<br />
                Research Dept: +1 (555) 123-4568<br />
                Partnerships: +1 (555) 123-4569<br />
                Fax: +1 (555) 123-4570
              </p>
            </div>
          </ScaleIn>
        </div>

        <FadeIn direction="up" delay={0.4}>
          <div className="section">
            <h2>Send Us a Message</h2>
            {submitted && (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '15px',
                borderRadius: '10px',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                ✓ Message sent successfully! We'll get back to you soon.
              </div>
            )}
            {error && (
              <div style={{
                background: '#ff4444',
                color: 'white',
                padding: '15px',
                borderRadius: '10px',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                {error}
              </div>
            )}

            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="What is this regarding?"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell us more about your inquiry..."
                />
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </FadeIn>

        <FadeIn direction="up" delay={0.5}>
          <div className="section newsletter-section">
            <h2>📬 Subscribe to Our Newsletter</h2>
            <p>Stay updated with the latest AI research and developments!</p>
            {newsletterSuccess && (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '15px',
                borderRadius: '10px',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                ✓ Successfully subscribed to newsletter!
              </div>
            )}
            {newsletterError && (
              <div style={{
                background: '#ff4444',
                color: 'white',
                padding: '15px',
                borderRadius: '10px',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: 'bold'
              }}>
                {newsletterError}
              </div>
            )}
            <form onSubmit={handleNewsletter} style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Enter your email"
                required
                style={{
                  flex: 1,
                  padding: '1rem',
                  border: '2px solid #667eea',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  color: '#333',
                  backgroundColor: '#fff'
                }}
              />
              <button type="submit" className="submit-btn" style={{ width: 'auto', padding: '1rem 2rem' }} disabled={loading}>
                {loading ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
          </div>
        </FadeIn>

        <FadeIn direction="up" delay={0.6}>
          <div className="section">
            <h2>Office Hours</h2>
            <p>
              Monday - Friday: 9:00 AM - 6:00 PM PST<br />
              Saturday: 10:00 AM - 4:00 PM PST<br />
              Sunday: Closed
            </p>
          </div>
        </FadeIn>

        <FadeIn direction="up" delay={0.7}>
          <div className="section">
            <h2>Career Opportunities</h2>
            <p>
              We're always looking for talented researchers, engineers, and innovators to
              join our team. Check out our careers page or send your resume to
              careers@aicoe.org.
            </p>
          </div>
        </FadeIn>
      </div>
    </div>
  );
};

export default Contact;
